import { CheckmarkFilled, CloseLarge, ErrorFilled } from '@carbon/icons-react';
import {
    colorSurfaceBannerWarning,
    colorSurfaceToastDark,
    colorTextOnToastDark,
    colorTextPrimary,
    colorTextWarning,
} from 'component/color';
import { StyleSheet, Text, View } from 'react-native';
import type { AppData } from 'util/appdata';
import { useAppData, useNotificationToasts } from 'util/appdataContext';
import type { FormatParams, NotificationToast, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';
import { generateRandomKey } from 'util/util';

import { HorizBox, HoverView } from './basics';
import { UtilityText } from './text';

type addNotificationToastProps = Omit<NotificationToast, 'id'> & {
    appData: AppData;
    type: 'info' | 'success' | 'error';
    duration?: number;
    label: TLabel;
    formatParams?: FormatParams;
    dismissable?: boolean;
};

export function addNotificationToast({
    appData,
    type,
    duration = 7000,
    label,
    dismissable = true,
    formatParams,
}: addNotificationToastProps) {
    const id = generateRandomKey(12);
    appData.addNotificationToast({ id, type, label, dismissable, formatParams });
    if (duration !== undefined) {
        setTimeout(() => {
            appData.removeNotificationToast(id);
        }, duration);
    }
}

interface NotificationToastProps {
    type: 'info' | 'success' | 'error';
    id: string;
    label: TLabel;
    formatParams?: FormatParams;
    dismissable?: boolean;
}

export function NotificationToast({ id, type, label, dismissable, formatParams }: NotificationToastProps) {
    const s = NotificationToastStyle;
    const appData = useAppData();
    return (
        <View
            style={[
                s.container,
                { backgroundColor: colorSurfaceToastDark },
                type === 'error' && { backgroundColor: colorSurfaceBannerWarning },
            ]}
        >
            <HorizBox gap={8}>
                {type === 'success' && <CheckmarkFilled color={colorTextOnToastDark} />}
                {type === 'error' && <ErrorFilled color={colorTextWarning} />}
                <UtilityText
                    color={type === 'error' ? colorTextPrimary : colorTextOnToastDark}
                    formatParams={formatParams}
                    label={label}
                    role='alert'
                    type='small'
                />
                {dismissable && (
                    <HoverView
                        ariaLabel={t('Dismiss')}
                        icon={CloseLarge}
                        onPress={() => {
                            appData.removeNotificationToast(id);
                        }}
                        role='button'
                        style={s.dismissButton}
                    >
                        <CloseLarge color={type === 'error' ? colorTextPrimary : colorTextOnToastDark} />
                    </HoverView>
                )}
            </HorizBox>
        </View>
    );
}

const NotificationToastStyle = StyleSheet.create({
    container: {
        padding: 20,
        borderRadius: 8,
        width: 335,
    },
    dismissButton: {
        marginLeft: 'auto',
    },
});

export function RenderNotificationToasts() {
    const s = RenderNotificationToastStyle;
    const toasts = useNotificationToasts();
    if (toasts.length === 0) {
        return null;
    }
    return (
        <View style={s.container}>
            {toasts.map((toast) => (
                <NotificationToast key={toast.id} {...toast} />
            ))}
        </View>
    );
}

const RenderNotificationToastStyle = StyleSheet.create({
    container: {
        position: 'absolute',
        bottom: 50,
        right: 50,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
});
